import React, { useEffect, useState } from 'react';
import { App, ConfigProvider, Flex, Layout, Spin, theme } from 'antd';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../../config/firebase';
import { getFirebaseErrorMessage } from '../../utils/firebaseErrorMessages';

function normalizePhone(raw: string | null | undefined): string {
  return String(raw ?? '').replace(/\D+/g, '');
}

function isValidPhoneDigits(p: string): boolean {
  return /^\d{9,15}$/.test(p);
}

const AutoLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const doAutoLogin = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const rawPhone = searchParams.get('phone');

        if (!rawPhone) {
          message.error(t('login.phoneRequired'));
          navigate('/login', { replace: true });
          return;
        }

        const phone = normalizePhone(rawPhone);

        if (!isValidPhoneDigits(phone)) {
          message.error(t('login.phoneInvalid'));
          navigate('/login', { replace: true });
          return;
        }

        const email = `${phone}@mou.com`;
        const password = phone;

        await signInWithEmailAndPassword(auth, email, password);

        const qUsers = query(collectionGroup(db, 'Users'), where('phone', '==', phone));
        const snap = await getDocs(qUsers);

        if (snap.empty) {
          await signOut(auth);
          message.error(t('login.phoneNotRegistered'));
          navigate('/login', { replace: true });
          return;
        }

        const firstActiveDoc = snap.docs.find((d) => {
          const data = d.data() as { active?: boolean } | undefined;
          return data?.active === true;
        });

        if (!firstActiveDoc) {
          await signOut(auth);
          message.error(t('login.accountInactive'));
          navigate('/login', { replace: true });
          return;
        }

        localStorage.setItem('rememberedPhone', phone);
        navigate('/chat', { replace: true });
      } catch (err: any) {
        const friendly =
          getFirebaseErrorMessage?.(err?.code) || t('login.loginError');
        message.error(friendly);
        try {
          await signOut(auth);
        } catch {
        }
        console.error('Auto login error:', err);
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    void doAutoLogin();
  }, [location.search, message, navigate, t]);

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#0b82d5',
          colorTextPlaceholder: 'rgba(255,255,255,0.65)',
        },
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        <Layout.Content>
          <Flex
            vertical
            align="center"
            justify="center"
            style={{ minHeight: '100vh', padding: 24 }}
          >
            <Spin size="large" spinning={loading} tip={t('login.loading')} />
          </Flex>
        </Layout.Content>
      </Layout>
    </ConfigProvider>
  );
};

export default AutoLoginPage;
